<template>
  <div style="margin-top: -15px; margin-bottom: 15px;">
      <div class="header-item last-mile-f-right" v-if="!isPickupCompleted && enableSaveButton">
        <div class="btn-inline">
          <Button size="mini" type="text" class="ml-0" @click="cancelChanges">{{$t('MDM_CANCEL')}}</Button>
          <Button size="mini" type="primary" class="ml-0" @click="savePickupDetails" :disabled="truckOutEnableCheck()">{{ $t('SAVE')}}</Button>
        </div>
      </div>
    <div>
      <Row>
        <Col :lg="24" :md="24">
          <span v-if="!canSubmitCurrentPickup">{{ $t('ACTION_CANNOT_BE_PERFORMED') }}</span>
          <Form v-else-if="!isPickupCompleted" :inline="true" class="form-default form-triple mt-15">
            <span v-for="(entry, index) in pickupFormData" :key="index">
              <FormItem v-if="showFormItem(entry)" :inline="true" :label="$t(entry.label)" :disabled="disableFormItem(index)">
                <DatePicker
                v-model="localFormData[entry.field]"
                type="datetime"
                :format="datePickerFormat"
                :placeholder="$t('INDENT_SELECT_DATE_AND_TIME')"
                :readonly="isReadOnly(entry.field)"
                :picker-options="getPickerOptions"
                size="small"
                />
              </FormItem>
            </span>
            <span v-if="truckOutEnableCheck()" class="ml-10" style="margin-bottom: 2px">
                  <Tooltip :open-delay="200" effect="dark" placement="bottom-start" popper-class="max-width" >
                    <div slot="content">{{ reason }} <output></output></div>
                    <svgicon name="red-circle-x-mark" width="15" height="15" :original="true" />
                  </Tooltip>
            </span>
          </Form>
          <span v-else>
            <div class="last-mile-flex-container">
              <div class="last-mile-row">
                <span class="last-mile-span">
                  {{ $t('INDENT_TRUCK_IN_DATE_TIME') }}&nbsp;:&nbsp;<strong>{{ getFormattedDate(currentPickup.arrived_at) }}</strong>
                </span>
                <span class="last-mile-span" v-if="isShipper && isLoadingAtSourceEnabled">
                  {{ $t('MDM_LOADING_START_TIME') }}&nbsp;:&nbsp;<strong>{{ getFormattedDate(currentPickup.loading_started_at) }}</strong>
                </span>
              </div>
              <div class="last-mile-row">
                <span class="last-mile-span" v-if="isShipper && isLoadingAtSourceEnabled">
                  {{ $t('MDM_LOADING_END_TIME') }}&nbsp;:&nbsp;<strong>{{ getFormattedDate(currentPickup.loading_ended_at) }}</strong>
                </span>
                <span class="last-mile-span">
                  {{ $t('INDENT_TRUCK_OUT_DATE_TIME') }}&nbsp;:&nbsp;<strong>{{ getFormattedDate(currentPickup.dispatched_at) }}</strong>
                </span>
              </div>
            </div>
          </span>
        </Col>
      </Row>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import { getDepotTypes } from 'pando-constants';
import { pickupFormData, dataTypes, editableFields, lastMileEvents } from '../../../constants/last-mile';
import { uniqBy } from 'lodash'

const { STANDARD } = getDepotTypes();

export default {
  name: 'LastMilePickupForm',
  props: {
    order: {
      type: Object,
      default: () => ({}),
    },
    getBackDateConfig: {
      type: Object,
      default: () => ({}),
    },
    isTruckInBackDateAllowed: {
      type: Boolean,
      default: () => false,
    },
    datePickerFormat: {
      type: String,
      default: () => 'dd-MM-yyyy hh:mmA',
    },
    isVendorDepotUser: {
      type: Boolean,
      default: () => false,
    },
    vendorDepots: {
      type: Array,
      default: () => [],
    },
    vendorDepotList: {
      type: Array,
      default: () => [],
    },
    isEligibleForPtlGrouping: {
      type: Boolean,
      default: () => false,
    },
    currentPickup: {
      type: Object,
      default: () => ({}),
    },
    currentIndex: {
      type: Number,
      default: () => -1,
    },
    canPerformAction: {
      type: Boolean,
      default: () => true,
    },
    isShipper: {
      type: Boolean,
      default: () => true,
    },
    currentUser: {
      type: Object,
      default: () => ({}),
    },
    getEntityDisplayName: {
      type: Function,
      required: true,
    },
    isDatesChanged: {
      type: Function,
      required: true,
    },
    getFormattedDate: {
      type: Function,
      required: true,
    },
    isLoadingAtSourceEnabled: {
      type: Boolean,
      default: () => true,
    },
    isMaterialInvoiceBeforeTruckOut: {
      type: Boolean,
      default: () => false,
    },
    isLRforTruckOut: {
      type: Boolean,
      default: () => false,
    }
  },
  created() {
    this.resetLocalFormData();
  },
  data() {
    return {
      dataTypes,
      pickupFormData,
      sectionType: {
        DEPOT: STANDARD,
      },
      localFormData: {
        arrived_at: null,
        loading_started_at: null,
        loading_ended_at: null,
        dispatched_at: null,
      },
      reason: ''
    };
  },
  computed: {
    isPickupCompleted() {
      return this.editableIndex === -1;
    },
    currentPickupId() {
      return this.currentPickup.unique_id || this.currentPickup._id;
    },
    sourceDepotMapper() {
      const mapper = {};
      this.gateList.forEach((sourceObj) => {
        mapper[sourceObj.unique_id] = sourceObj.location_id;
      });
      return mapper;
    },
    canSubmitCurrentPickup() {
      if (!this.isShipper) return true;
      if (this.isVendorDepotUser && this.vendorDepotList && this.vendorDepotList.length) {
        if (this.sourceDepotMapper &&
          this.sourceDepotMapper[this.currentPickupId] &&
          this.vendorDepotList.includes(this.sourceDepotMapper[this.currentPickupId])
        ) return true;
        return false;
      } return !!((this.sourceDepotMapper &&
          this.sourceDepotMapper[this.currentPickupId]));
    },
    gateList() {
      return this.order.sources.filter(this.hasDepotGates);
    },
    getPickerOptions() {
      const editableField = this.editableField;
      const currentPickup = this.currentPickup;
      if (editableField === editableFields.ARRIVED) {
        const assignedDate = !this.isTruckInBackDateAllowed ? this.order.assigned_at : null;
        return {
          disabledDate(time) {
            return time.getTime() > Date.now() ||
              time.getTime() < new Date(assignedDate).getTime() - 8.64e7;
          },
        };
      } else if (editableField === editableFields.DISPATCHED) {
        return {
          disabledDate(time) {
            return (
              time.getTime() > Date.now() ||
              time.getTime() < new Date(currentPickup.loading_ended_at || currentPickup.arrived_at).getTime() - 8.64e7
            );
          },
        };
      } else if (editableField === editableFields.LOADING_START) {
        return {
          disabledDate(time) {
            return (
              time.getTime() > Date.now() ||
              time.getTime() < new Date(currentPickup.arrived_at).setHours(0, 0, 0, 0)
            );
          },
        };
      }
      return {
        disabledDate(time) {
          return (
            time.getTime() > Date.now() ||
            time.getTime() < new Date(currentPickup.loading_started_at).setHours(0, 0, 0, 0)
          );
        },
      };
    },
    editableIndex() {
      return this.pickupFormData.findIndex(item => !this.currentPickup[item.field] && this.showFormItem(item));
    },
    enableSaveButton() {
      if (!this.canPerformAction) return false;
      const editableField = this.editableField;
      if (editableField) {
        return this.isDatesChanged(this.localFormData[editableField], this.currentPickup[editableField]);
      }
      return false;
    },
    editableField() {
      if (this.editableIndex === -1) return null;
      return this.pickupFormData[this.editableIndex].field;
    },
  },
  methods: {
    showFormItem(item) {
      const canShowField = item.validation ? this[item.validation] : true;
      return item.shipper_only ? canShowField && this.isShipper : canShowField;
    },
    truckOutEnableCheck(){
      let isEnable = false;
      if(this.editableIndex === lastMileEvents.TRUCKOUT && this.isMaterialInvoiceBeforeTruckOut && !this.currentPickup.is_material_invoice_updated){
        this.reason = this.$t('INDENT_MATERIAL_INV_NOT_AVAILABLE');
        isEnable = true;
      } else if(this.editableIndex === lastMileEvents.TRUCKOUT && this.isLRforTruckOut && !this.currentPickup.is_lr_updated){
        this.reason = this.$t('INDENT_LR_NOT_AVAILABLE');
        isEnable = true;
      }
      return isEnable;
    },
    disableFormItem(index) {
      if((this.isMaterialInvoiceBeforeTruckOut || this.isLRforTruckOut) && this.canPerformAction && this.editableIndex === index){
        return index === lastMileEvents.TRUCKOUT ? this.truckOutEnableCheck() : !this.canPerformAction || this.editableIndex !== index;
      }
      return !this.canPerformAction || this.editableIndex !== index;
    },
    avoidBackDate(field) {
      if (field === editableFields.ARRIVED) {
        return this.getBackDateConfig.truck_in;
      } else if (field === editableFields.DISPATCHED) {
        return this.getBackDateConfig.truck_out;
      }
      return this.getBackDateConfig.loading_date;
    },
    isReadOnly(field) {
      return !this.canPerformAction || !!(this.avoidBackDate(field) || this.currentPickup[field]);
    },
    hasDepotGates(source) {
      const hasGateAccess = (this.currentUser && this.currentUser.gate_associates_ids) &&
        this.currentUser.gate_associates_ids.length;
      return source.type === this.sectionType.DEPOT && hasGateAccess ?
        this.currentUser.gate_associates_ids.includes(source.gate_id) :
        true;
    },
    truckin() {
      if (!this.isTruckInBackDateAllowed && (this.localFormData.arrived_at < new Date(this.order.assigned_at))) {
        this.$errorMessage({ message: this.$t('INDENT_TRUCKIN_AFTER_ASSIGNED_ERROR_MSG') });
        return;
      }
      const data = {};
      data.unique_id = this.currentPickupId;
      data.arrived_at = (this.avoidBackDate(editableFields.ARRIVED)) ? new Date() : this.localFormData.arrived_at;
      this.$emit('truckin', data);
    },
    startLoad() {
      if (this.localFormData.loading_started_at > new Date()) {
        this.$errorMessage({ message: this.$t('INDENT_LOADING_FUTURE_ERROR') });
        return;
      } else if (
        this.currentPickup.arrived_at &&
          this.localFormData.loading_started_at < new Date(this.currentPickup.arrived_at).setSeconds(0, 0)
      ) {
        this.$errorMessage({ message: this.$t('INDENT_LOADING_TRUCK_IN_ERROR') });
        return;
      }
      const data = { unique_id: this.currentPickupId };
      data.load_time = this.localFormData.loading_started_at;
      this.$emit('start-load', data);
    },
    endLoad() {
      if (!this.localFormData.loading_ended_at) {
        this.$errorMessage({ message: this.$t('INDENT_SELECT_DATE_AND_TIME') });
        return;
      }
      if (this.localFormData.loading_ended_at > new Date()) {
        this.$errorMessage({ message: this.$t('INDENT_LOADING_FUTURE_ERROR') });
        return;
      } else if (
        this.currentPickup.loading_started_at &&
          this.localFormData.loading_ended_at < new Date(this.currentPickup.loading_started_at).setSeconds(0, 0)
      ) {
        this.$errorMessage({ message: this.$t('INDENT_LOADING_END_BEFORE_START_ERROR') });
        return;
      }
      const data = {};
      data.load_time = this.localFormData.loading_ended_at;
      data.unique_id = this.currentPickupId;
      this.$emit('end-load', data);
    },
    truckout() {
      const data = {};
      data.unique_id = this.currentPickupId;
      data.dispatched_at = this.avoidBackDate(editableFields.DISPATCHED)
        ? new Date()
        : this.localFormData.dispatched_at;
      this.$emit('truckout', { data, name: this.getEntityDisplayName(this.currentPickup) });
    },
    savePickupDetails() {
      const editedField = this.editableField;
      if (editedField === editableFields.ARRIVED) {
        this.truckin();
      } else if (editedField === editableFields.LOADING_START) {
        this.startLoad();
      } else if (editedField === editableFields.LOADING_END) {
        this.endLoad();
      } else if (editedField === editableFields.DISPATCHED) {
        this.truckout();
      }
    },
    cancelChanges() {
      this.$emit('cancel');
      this.resetLocalFormData();
    },
    resetLocalFormData() {
      this.localFormData = cloneDeep(this.currentPickup);
      const editableField = this.editableField;
      if (editableField && !this.localFormData[editableField]) {
        this.localFormData[editableField] = new Date();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.last-mile-f-right {
  position: absolute;
  top: 24px;
  right: 0px;
}
</style>

